import React from 'react';

import { S3_BUCKET_URL } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount';
import { webCampaign } from '@/modules/webCampaign';
import { useLayoutSelections } from '@/pages/Onboarding/hooks/useLayoutSelections';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { MainImageHeader } from '@/pages/Paywall/components/MainImageHeader';
import { PersonalizedHeader } from '@/pages/Paywall/components/PersonalizedHeader';
import { PaywallProFeatures } from '@/pages/Paywall/components/ProFeatures';
import {
  PLANS_ACCESS,
  SAFE_PLANS_ACCESS,
  VIP_PLANS_ACCESS,
} from '@/pages/Paywall/components/ProFeatures/constant';

import {
  supportedSexyBodyShapes,
  supportedSexyHairColors,
} from '../../constants';

import {
  MainImageContentStyles,
  MainImageStyles,
  TitleHighlightAlt,
} from './MainImage.styles';

interface Props {
  imageSrc?: string;
  extraHeight?: number;
}

export const SectionMainImage: React.FC<Props> = ({
  imageSrc,
  extraHeight,
}) => {
  const { ageGeneration, bodyShape, hairColor } = useLayoutSelections();
  const { user } = useAccount();

  const getBackgroundSrc = () => {
    if (imageSrc) {
      return imageSrc;
    }

    if (webCampaign.setup.paywallImagesVariant === 'v6-sexy') {
      // TODO should be updated with link from s3
      return `../assets/paywall/v6-sexy/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }

    const isCrutchOnly4NsfwGirl =
      webCampaign.setup.paywallNotes.includes('4-nsfw-girl') &&
      hairColor &&
      supportedSexyHairColors.includes(hairColor) &&
      bodyShape &&
      supportedSexyBodyShapes.includes(bodyShape);

    if (isCrutchOnly4NsfwGirl) {
      // TODO should be updated with link from s3
      return `../assets/paywall/v6-sexy/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    } else {
      return `${S3_BUCKET_URL}/paywall/v6/${ageGeneration}_${hairColor}_${bodyShape}.png`;
    }
  };

  const getPlansAccess = () => {
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.VIP_PLANS_ACCESS)
    ) {
      return VIP_PLANS_ACCESS;
    }
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.SAFE_PLANS_ACCESS)
    ) {
      return SAFE_PLANS_ACCESS;
    }
    return PLANS_ACCESS;
  };

  const DefaultHeader = () => (
    <OwnAiTitle className="main-image-title">
      Get your plan and enjoy
      <br />
      <OwnAiTitleHighlight>unlimited</OwnAiTitleHighlight> chats
    </OwnAiTitle>
  );

  const OneNightStandHeader = () => (
    <OwnAiTitle className="main-image-title">
      Live the night of your
      <br />
      <OwnAiTitleHighlight>dreams...</OwnAiTitleHighlight>
    </OwnAiTitle>
  );

  const ForestHeader = () => {
    const username = user?.name || '';
    return (
      <OwnAiTitle className="main-image-title">
        The deeper you go{username ? ',' : '.'}
        {username && (
          <>
            <br />
            {username}.
          </>
        )}
        <br />
        <TitleHighlightAlt>the closer it feels.</TitleHighlightAlt>
      </OwnAiTitle>
    );
  };

  const getTitle = () => {
    if (
      webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.PERSONALIZED_HEADER)
    ) {
      return <PersonalizedHeader />;
    }
    if (webCampaign.setup.paywallNotes.includes(PAYWALL_NOTES.FOREST_TITLE)) {
      return <ForestHeader />;
    }
    if (webCampaign.setup.paywallNotes.includes('one-night-stand')) {
      return <OneNightStandHeader />;
    }
    return <DefaultHeader />;
  };

  return (
    <MainImageStyles
      backgroundSrc={getBackgroundSrc()}
      extraHeight={extraHeight}
    >
      <MainImageHeader />
      <MainImageContentStyles>
        {getTitle()}
        <PaywallProFeatures features={getPlansAccess()} />
      </MainImageContentStyles>
    </MainImageStyles>
  );
};
